/**
 * RisikoprofilFeldInput - renders ONE catalog field of the generelles Risikoprofil, by typ
 * (text / textarea / number / boolean / select / multiselect). Read mode shows the formatted
 * value; edit mode shows the matching control. Option labels resolve via risikoprofilOptionLabel.
 */

import { risikoprofilOptionLabel, RISIKOPROFIL_TEXT_MAX, RISIKOPROFIL_TEXTAREA_MAX, RISIKOPROFIL_NUMBER_MAX } from '@brokly/shared';
import type { RisikoprofilFeldBasis } from '@brokly/shared';

/** Option-label resolver (defaults to the Kunde map; Slice 2 passes the Police one). */
type OptionLabelFn = (value: string) => string;

function formatValue(def: RisikoprofilFeldBasis, value: unknown, optionLabel: OptionLabelFn): string {
  if (value === null || value === undefined || value === '') return '-';
  switch (def.typ) {
    case 'boolean':
      return value ? 'Ja' : 'Nein';
    case 'select':
      return optionLabel(String(value));
    case 'multiselect':
      return Array.isArray(value) && value.length > 0
        ? value.map((v) => optionLabel(String(v))).join(', ')
        : '-';
    case 'number':
      return String(value);
    default:
      return String(value);
  }
}

export function RisikoprofilFeldInput({
  def,
  value,
  editing,
  disabled,
  onChange,
  optionLabel = risikoprofilOptionLabel,
}: {
  // Widened to the shared base type (P2 Slice 2): the SAME input renders a Kunde field or a Police
  // branch field. The Kunde `profil_typ`/`kategorie` extras are not read here.
  def: RisikoprofilFeldBasis;
  value: unknown;
  editing: boolean;
  disabled?: boolean;
  onChange: (value: unknown) => void;
  /** Resolve a select/multiselect option value to its label (Police tab passes policeRisikoOptionLabel). */
  optionLabel?: OptionLabelFn;
}) {
  const label = (
    <span className="text-xs font-medium text-muted-foreground">
      {def.label}
      {def.einheit ? ` (${def.einheit})` : ''}
    </span>
  );

  if (!editing) {
    return (
      <div className="flex flex-col gap-0.5" data-testid={`rp-field-${def.key}`}>
        {label}
        <span className="text-sm text-foreground">{formatValue(def, value, optionLabel)}</span>
      </div>
    );
  }

  const inputId = `rp-input-${def.key}`;
  return (
    <div className="flex flex-col gap-1" data-testid={`rp-field-${def.key}`}>
      <label htmlFor={inputId}>{label}</label>
      {def.typ === 'boolean' ? (
        <input
          id={inputId}
          type="checkbox"
          disabled={disabled}
          checked={value === true}
          onChange={(e) => onChange(e.target.checked)}
          className="size-4"
        />
      ) : def.typ === 'number' ? (
        <input
          id={inputId}
          type="number"
          disabled={disabled}
          max={RISIKOPROFIL_NUMBER_MAX}
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          className="rounded-md border bg-background px-2 py-1 text-sm"
        />
      ) : def.typ === 'date' ? (
        <input
          id={inputId}
          type="date"
          disabled={disabled}
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
          className="rounded-md border bg-background px-2 py-1 text-sm"
        />
      ) : def.typ === 'textarea' ? (
        <textarea
          id={inputId}
          disabled={disabled}
          maxLength={RISIKOPROFIL_TEXTAREA_MAX}
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
          className="min-h-16 rounded-md border bg-background px-2 py-1 text-sm"
        />
      ) : def.typ === 'select' ? (
        <select
          id={inputId}
          disabled={disabled}
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
          className="rounded-md border bg-background px-2 py-1 text-sm"
        >
          <option value="">-</option>
          {(def.options ?? []).map((opt) => (
            <option key={opt} value={opt}>
              {optionLabel(opt)}
            </option>
          ))}
        </select>
      ) : def.typ === 'multiselect' ? (
        <select
          id={inputId}
          multiple
          disabled={disabled}
          value={Array.isArray(value) ? (value as string[]) : []}
          onChange={(e) =>
            onChange(Array.from(e.target.selectedOptions).map((o) => o.value))
          }
          className="rounded-md border bg-background px-2 py-1 text-sm"
        >
          {(def.options ?? []).map((opt) => (
            <option key={opt} value={opt}>
              {optionLabel(opt)}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={inputId}
          type="text"
          disabled={disabled}
          maxLength={RISIKOPROFIL_TEXT_MAX}
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
          className="rounded-md border bg-background px-2 py-1 text-sm"
        />
      )}
    </div>
  );
}
